import React, { useEffect, useState } from 'react';
import { Pressable, Box, useToken, useColorMode, ChevronRightIcon } from 'native-base';
import { Text, PencilIcon, QuestionMarkOutlineIcon } from '../../atoms';
import { borderRadius, borderWidth, sizes } from '../../style';
import { variantStyles } from './buttonVariantStyles';
import { AnimatedButtonContent } from './AnimatedButtonContent';
export function CustomButtonTemplate({ autoWidth = false, text = 'Label', showEditIcon = false, showHelpIcon = false, showNextIcon = false, styleData = variantStyles.primary, isDisabled = false, testIDWrapper = 'default-wrapper-id-for-btn', testIDText = 'default-text-id-for-btn', testIDIcon = 'default-icon-id-for-btn', testIDRightIcon = 'default-right-icon-id-for-btn', leftIcon = null, rightIcon = null, customLeftIcon = null, customRightIcon = null, textTransition = false, textTransitionDuration = 300, onPressButton = () => null, onPressIn = () => null, }) {
    const { colorMode } = useColorMode();
    const [isPressed, setIsPressed] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    // used to fix focus bug on web
    const [isHoverIn, setIsHoverIn] = useState(false);
    const [bgColor, bgColorPressed, bgColorFocused, bgColorHover, bgColorDisabled] = useToken('colors', [
        styleData.default.bgColor,
        styleData.pressed.bgColor,
        styleData.focused.bgColor,
        styleData.hover.bgColor,
        styleData.disabled.bgColor,
    ]);
    const [currentBgColor, setCurrentBgColor] = useState(bgColor);
    const [currentBorderColor, setCurrentBorderColor] = useState(styleData.default.borderColor);
    const [currentTextColor, setCurrentTextColor] = useState(styleData.default.textColor);
    useEffect(() => {
        if (isHovered) {
            setCurrentBgColor(bgColorHover);
            setCurrentBorderColor(styleData.hover.borderColor);
            setCurrentTextColor(styleData.hover.textColor);
        }
        else if (isFocused && !isHoverIn) {
            setCurrentBgColor(bgColorFocused);
            setCurrentBorderColor(styleData.focused.borderColor);
            setCurrentTextColor(styleData.focused.textColor);
        }
        else if (isPressed) {
            setCurrentBgColor(bgColorPressed);
            setCurrentBorderColor(styleData.pressed.borderColor);
            setCurrentTextColor(styleData.pressed.textColor);
        }
        else {
            setCurrentBgColor(bgColor);
            setCurrentBorderColor(styleData.default.borderColor);
            setCurrentTextColor(styleData.default.textColor);
        }
    }, [
        isPressed,
        isHovered,
        isFocused,
        isHoverIn,
        bgColorHover,
        styleData,
        bgColorFocused,
        bgColorPressed,
        bgColor,
    ]);
    // fixes not updating when dark mode is enabled
    useEffect(() => {
        // @ts-expect-error TODO: Fix in NS-4735
        setCurrentBgColor(isGradient ? bgColor : styleData.default.bgColor);
        setCurrentBorderColor(styleData.default.borderColor);
        setCurrentTextColor(styleData.default.textColor);
    }, [colorMode]);
    const isGradient = typeof currentBgColor === 'object';
    // handle web gradient
    const refWebStyle = React.useRef(null);
    const disabledBgColor = bgColorDisabled;
    const webBgGradientOrColor = getWebBgGradientOrColor(isDisabled, isGradient);
    const webBgColorPressed = 'linear-gradient(' + bgColorPressed + ',' + bgColorPressed + ')';
    const styleObj = {
        backgroundImage: isPressed || isHovered ? webBgColorPressed : webBgGradientOrColor,
    };
    refWebStyle.current?.setNativeProps({
        style: styleObj,
    });
    const borderRadiusConfig = isFocused && !isHoverIn && !isPressed ? borderRadius.outerButton : borderRadius.innerButton;
    const borderWidthConfig = isFocused && !isHoverIn && !isPressed ? borderWidth.outerButton : 0;
    const borderColorConfig = getBorderColorConfig(isDisabled, isFocused);
    const backgroundConfig = getBackgroundConfig(isDisabled);
    function getWebBgGradientOrColor(disabled, usesGradient) {
        if (disabled) {
            return 'linear-gradient(' + disabledBgColor + ',' + disabledBgColor + ')';
        }
        if (usesGradient) {
            // @ts-expect-error TODO: Fix in NS-4735
            return 'linear-gradient(' + bgColor.colors[0] + ',' + bgColor.colors[1] + ')';
        }
        return 'linear-gradient(' + bgColor + ',' + bgColor + ')';
    }
    function getBackgroundConfig(disabled) {
        if (disabled) {
            return styleData.disabled.bgColor;
        }
        else if (!isGradient) {
            return currentBgColor;
        }
        else {
            return {
                linearGradient: {
                    colors: [currentBgColor.colors[1], currentBgColor.colors[0]],
                    start: [0, currentBgColor.originalLocations[0]],
                    end: [0, currentBgColor.originalLocations[1]],
                },
            };
        }
    }
    function getBorderColorConfig(disabled, focused) {
        if (disabled) {
            return styleData.disabled.borderColor;
        }
        if (focused) {
            return styleData.focused.borderColor;
        }
        return currentBorderColor;
    }
    return (React.createElement(Box, { overflow: 'hidden', w: autoWidth ? 'auto' : '100%', borderWidth: borderWidthConfig, borderRadius: borderRadiusConfig, borderColor: styleData.focused.focusedBorderColor, accessible: true, accessibilityRole: "button" },
        React.createElement(Box, { ref: refWebStyle, testID: testIDWrapper, bg: backgroundConfig },
            React.createElement(Pressable, { minH: sizes.buttonHeight, borderWidth: styleData.outline.width, borderRadius: borderRadius.innerButton, justifyContent: 'center', borderColor: borderColorConfig, onPressOut: () => setIsFocused(false), onPress: () => {
                    setIsFocused(false);
                    onPressButton();
                }, onPressIn: onPressIn, disabled: isDisabled, onBlur: () => setIsFocused(false), onHoverIn: () => setIsHoverIn(true), onHoverOut: () => setIsHoverIn(false), onFocus: () => setIsFocused(true) }, ({ isPressed, isHovered }) => {
                useEffect(() => {
                    setIsPressed(isPressed);
                    setIsHovered(isHovered);
                }, [isPressed, isHovered]);
                return (React.createElement(AnimatedButtonContent, { textTransition: textTransition, textTransitionDuration: textTransitionDuration, text: text, autoWidth: autoWidth, autoButtonPadding: sizes.autoButtonPadding },
                    React.createElement(LeftIconSection, { customLeftIcon: customLeftIcon, leftIcon: leftIcon, showEditIcon: showEditIcon, showHelpIcon: showHelpIcon, isDisabled: isDisabled, styleData: styleData, currentTextColor: currentTextColor, testIDIcon: testIDIcon }),
                    React.createElement(Text, { textAlign: 'center', testID: testIDText, fontToken: styleData.default.fontToken, color: isDisabled ? styleData.disabled.textColor : currentTextColor }, text),
                    React.createElement(RightIconSection, { customRightIcon: customRightIcon, rightIcon: rightIcon, showNextIcon: showNextIcon, isDisabled: isDisabled, styleData: styleData, currentTextColor: currentTextColor, testIDRightIcon: testIDRightIcon })));
            }))));
}
function LeftIconSection({ customLeftIcon, leftIcon, showEditIcon, showHelpIcon, testIDIcon = 'default-icon-id-for-btn', isDisabled, styleData, currentTextColor, }) {
    const LeftIcon = leftIcon;
    switch (true) {
        case !!customLeftIcon:
            return customLeftIcon ?? null;
        case !!leftIcon:
            return (React.createElement(LeftIcon, { size: sizes.buttonIconSize, testID: testIDIcon, minW: sizes.buttonIconMinW, mr: sizes.buttonIconSpace, color: isDisabled ? styleData?.disabled.textColor : currentTextColor }));
        case !!showEditIcon:
            return (React.createElement(PencilIcon, { size: sizes.buttonIconSize, testID: testIDIcon, minW: sizes.buttonIconMinW, mr: sizes.buttonIconSpace, 
                // @ts-expect-error currentTextColor is not a token
                color: isDisabled ? styleData?.disabled.textColor : currentTextColor }));
        case !!showHelpIcon:
            return (React.createElement(QuestionMarkOutlineIcon, { size: sizes.buttonIconSize, mr: sizes.textButtonIconSpace, testID: testIDIcon, minW: sizes.buttonIconMinW, 
                // @ts-expect-error currentTextColor is not a token
                color: isDisabled ? styleData?.disabled.textColor : currentTextColor }));
        default:
            return null;
    }
}
function RightIconSection({ customRightIcon, rightIcon, showNextIcon, testIDRightIcon, isDisabled, styleData, currentTextColor, }) {
    const RightIcon = rightIcon;
    switch (true) {
        case !!customRightIcon:
            return customRightIcon ?? null;
        case !!rightIcon:
            return (React.createElement(RightIcon, { size: sizes.buttonIconSize, testID: testIDRightIcon, minW: sizes.buttonIconMinW, ml: sizes.buttonIconSpace, color: isDisabled ? styleData?.disabled.textColor : currentTextColor }));
        case !!showNextIcon:
            return (React.createElement(ChevronRightIcon, { size: sizes.buttonIconSize / 2, testID: testIDRightIcon, ml: sizes.textButtonIconSpace, minW: sizes.buttonIconMinW / 2, color: isDisabled ? styleData?.disabled.textColor : currentTextColor }));
        default:
            return null;
    }
}
